import React, { useState } from 'react';
import { X, Check, Loader2, ExternalLink, Sparkles, AlertTriangle } from 'lucide-react';
import apiClient from '../../../lib/apiClient';
import TransactionListModal from '../assessment/TransactionListModal';

// ============================================================================
// SIGNAL MODAL (Policy Decision Configuration + Readiness Assessment)
// ============================================================================

const SignalModal = ({ signal, data, suggestion, onClose, onSave, saving, departmentSelected, departmentId, departmentName }) => {
  // Transaction drill-in state (mirror of CapabilityModal pattern).
  const [showTransactions, setShowTransactions] = useState(false);

  const fetchTransactions = async () => {
    if (!departmentId || !signal?.id) return { transactions: [], count: 0, totalSpend: 0 };
    try {
      return await apiClient.get(
        `/api/zero-trust/departments/${departmentId}/signal/${signal.id}/transactions`
      );
    } catch (err) {
      console.error('Error fetching signal transactions:', err);
      return { transactions: [], count: 0, totalSpend: 0 };
    }
  };

  // Auto-populate from suggestion if no existing data
  const getInitialVendor = () => {
    if (data.vendor) return data.vendor;
    if (!suggestion?.topVendor) return '';
    // Check if the suggested vendor exists in vendorOptions
    const match = signal.vendorOptions?.find(v =>
      v.vendor.toLowerCase() === suggestion.topVendor.toLowerCase()
    );
    return match ? match.vendor : '';
  };

  const getInitialProduct = () => {
    if (data.product) return data.product;
    if (!suggestion?.topProduct) return '';
    const vendorName = getInitialVendor();
    if (!vendorName) return '';
    // Check if the suggested product exists for this vendor
    const vendorData = signal.vendorOptions?.find(v => v.vendor === vendorName);
    const match = vendorData?.products?.find(p =>
      p.toLowerCase().includes(suggestion.topProduct.toLowerCase()) ||
      suggestion.topProduct.toLowerCase().includes(p.toLowerCase())
    );
    return match || '';
  };

  const [formData, setFormData] = useState({
    vendor: getInitialVendor(),
    product: getInitialProduct(),
    integrationStatus: data.integrationStatus || 'not_configured',
    configNotes: data.configNotes || '',
    assessmentResponses: data.assessmentResponses || {},
    integrationChecks: data.integrationChecks || {},
    lastUpdated: data.lastUpdated || ''
  });

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVendorChange = (vendorName) => {
    setFormData(prev => ({ ...prev, vendor: vendorName, product: '' }));
  };

  // Get products for selected vendor
  const selectedVendorData = signal.vendorOptions?.find(v => v.vendor === formData.vendor);
  const availableProducts = selectedVendorData?.products || [];

  const handleAssessmentChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      assessmentResponses: { ...prev.assessmentResponses, [questionId]: value }
    }));
  };

  const handleCheckChange = (checkItem) => {
    setFormData(prev => ({
      ...prev,
      integrationChecks: {
        ...prev.integrationChecks,
        [checkItem]: !prev.integrationChecks[checkItem]
      }
    }));
  };

  const handleSave = () => {
    onSave({ ...formData, lastUpdated: new Date().toISOString() });
  };

  // Calculate readiness score based on assessment responses
  const calculateReadinessScore = () => {
    const responses = formData.assessmentResponses;
    let score = 0;
    let total = 0;

    signal.assessmentQuestions.forEach(q => {
      if (responses[q.id]) {
        total++;
        if (q.type === 'choice') {
          if (responses[q.id] === 'Yes') score += 100;
          else if (responses[q.id] === 'Partial') score += 50;
        } else if (q.type === 'percentage') {
          score += parseInt(responses[q.id]) || 0;
        }
      }
    });

    return total > 0 ? Math.round(score / total) : null;
  };

  const readinessScore = calculateReadinessScore();

  const integrationStatusOptions = [
    { value: 'not_configured', label: 'Not Configured', color: '#ef4444' },
    { value: 'partial', label: 'Partially Integrated', color: '#f59e0b' },
    { value: 'fully_integrated', label: 'Fully Integrated', color: '#10b981' }
  ];

  return (
    <div className="zta-modal-backdrop" onClick={onClose}>
      <div className="zta-modal zta-modal--signal" onClick={e => e.stopPropagation()}>
        <div className="zta-modal-header">
          <div>
            <h2 className="zta-modal-title">{signal.label}</h2>
            <p className="zta-modal-subtitle">
              {signal.question}
              {departmentName && ` · ${departmentName}`}
            </p>
          </div>
          <button className="zta-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="zta-modal-body zta-modal-body--scrollable">
          {/* Description */}
          <div className="zta-signal-description">
            <p>{signal.description}</p>
          </div>

          {/* Readiness Score Badge */}
          {readinessScore !== null && (
            <div className={`zta-readiness-score ${readinessScore >= 75 ? 'zta-readiness-score--good' : readinessScore >= 50 ? 'zta-readiness-score--partial' : 'zta-readiness-score--low'}`}>
              <span className="zta-readiness-label">Signal Readiness</span>
              <span className="zta-readiness-value">{readinessScore}%</span>
            </div>
          )}

          {/* Configuration Section */}
          {departmentSelected && (
            <>
              {/* Suggestion banner — drill-in to source transactions so the
                  reviewer can verify the spend and flag misclassified rows. */}
              {suggestion?.detected?.length > 0 && (
                <div className="zta-suggestion-banner">
                  <div className="zta-suggestion-banner-content">
                    <Sparkles size={14} />
                    <span>
                      {data.vendor ? 'Purchase history: ' : 'Pre-populated from purchase history: '}
                      <strong>{suggestion.topVendor}</strong>
                      {suggestion.topProduct && <> ({suggestion.topProduct})</>}
                      {' '}- ${Math.round((suggestion.totalSpend || 0)).toLocaleString()} spent
                    </span>
                  </div>
                  {departmentId && (
                    <button
                      type="button"
                      className="zta-suggestion-view-btn"
                      onClick={() => setShowTransactions(true)}
                      title="View the procurement rows that back this spend figure — flag any that look misclassified."
                    >
                      <ExternalLink size={12} />
                      View Transactions
                    </button>
                  )}
                </div>
              )}

              <div className="zta-signal-section">
                <h3>Signal Configuration</h3>

                {/* Vendor Selection */}
                <div className="zta-modal-field">
                  <label>Vendor Providing This Signal</label>
                  <select
                    value={formData.vendor}
                    onChange={e => handleVendorChange(e.target.value)}
                    className="zta-modal-select"
                  >
                    <option value="">Select a vendor...</option>
                    {signal.vendorOptions?.map(v => (
                      <option key={v.vendor} value={v.vendor}>{v.vendor}</option>
                    ))}
                  </select>
                </div>

                {/* Product Selection (dependent on vendor) */}
                {formData.vendor && (
                  <div className="zta-modal-field">
                    <label>Product</label>
                    <select
                      value={formData.product}
                      onChange={e => handleFieldChange('product', e.target.value)}
                      className="zta-modal-select"
                    >
                      <option value="">Select a product...</option>
                      {availableProducts.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Integration Status */}
                <div className="zta-modal-field">
                  <label>Integration Status</label>
                  <div className="zta-status-buttons">
                    {integrationStatusOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`zta-status-btn ${formData.integrationStatus === opt.value ? 'active' : ''}`}
                        onClick={() => handleFieldChange('integrationStatus', opt.value)}
                        style={{ '--status-color': opt.color }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Integration Checklist */}
                <div className="zta-modal-field">
                  <label>Integration Checklist</label>
                  <div className="zta-checklist">
                    {signal.integrationChecks.map((check, i) => (
                      <label key={i} className="zta-check-item">
                        <input
                          type="checkbox"
                          checked={formData.integrationChecks[check] || false}
                          onChange={() => handleCheckChange(check)}
                        />
                        <span>{check}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Assessment Section */}
              <div className="zta-signal-section">
                <h3>Readiness Assessment</h3>

                {signal.assessmentQuestions.map(q => (
                  <div key={q.id} className="zta-modal-field">
                    <label>{q.question}</label>
                    {q.type === 'choice' && (
                      <div className="zta-choice-buttons">
                        {q.options.map(opt => (
                          <button
                            key={opt}
                            className={`zta-choice-btn ${formData.assessmentResponses[q.id] === opt ? 'active' : ''}`}
                            onClick={() => handleAssessmentChange(q.id, opt)}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                    {q.type === 'percentage' && (
                      <div className="zta-percentage-input">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={formData.assessmentResponses[q.id] || 0}
                          onChange={e => handleAssessmentChange(q.id, e.target.value)}
                          className="zta-range-input"
                        />
                        <span className="zta-percentage-value">
                          {formData.assessmentResponses[q.id] || 0}%
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="zta-modal-field">
                <label>Configuration Notes</label>
                <textarea
                  value={formData.configNotes}
                  onChange={e => handleFieldChange('configNotes', e.target.value)}
                  placeholder="Integration details, issues, contacts..."
                  className="zta-modal-textarea"
                  rows={3}
                />
              </div>
            </>
          )}

          {!departmentSelected && (
            <div className="zta-gov-no-dept">
              <AlertTriangle size={16} />
              Select a department to configure this signal
            </div>
          )}
        </div>

        <div className="zta-modal-footer">
          <button className="zta-modal-btn zta-modal-btn--secondary" onClick={onClose}>
            Close
          </button>
          {departmentSelected && (
            <button
              className="zta-modal-btn zta-modal-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
              Save Changes
            </button>
          )}
        </div>

        {/* Transaction drill-in — reviewer can verify/flag the procurement
            rows that back the AI suggestion for this signal. */}
        <TransactionListModal
          isOpen={showTransactions}
          onClose={() => setShowTransactions(false)}
          capabilityCode={signal.id}
          capabilityName={signal.label || signal.id}
          vendor={suggestion?.topVendor}
          product={suggestion?.topProduct}
          fetchTransactions={fetchTransactions}
        />
      </div>
    </div>
  );
};

export default SignalModal;
